import { z } from 'zod';
import type { AssistantReportDefinition } from '../entities/assistant';

export type { AssistantReportDefinition } from '../entities/assistant';

export const RUN_REPORTS_REPORT_DEFINITION_QUERY_PARAM = 'reportDefinition';
export const RUN_REPORTS_ROUTE_INTENT_QUERY_PARAM = 'routeIntent';
export const RUN_REPORTS_ROUTE_INTENT_SOURCE_FRAGMENT_QUERY_PARAM = 'routeIntentSource';
export const RUN_REPORTS_VISUALIZATION_VIEW_QUERY_PARAM = 'visualizationView';
export const RUN_REPORTS_VISUALIZATION_CHART_TYPE_QUERY_PARAM = 'visualizationChartType';
export const RUN_REPORTS_VISUALIZATION_HASH = 'run-reports-visualization';
export const RUN_REPORTS_VISUALIZATION_ROUTE_INTENT_KIND = 'run-reports-visualization';

export const RunReportsVisualizationViewSchema = z.enum(['chart', 'advanced', 'graph']);
export type RunReportsVisualizationView = z.infer<typeof RunReportsVisualizationViewSchema>;

export const RunReportsRouteIntentChartTypeSchema = z.enum(['bar', 'line', 'pie', 'scatter']);
export type RunReportsRouteIntentChartType = z.infer<typeof RunReportsRouteIntentChartTypeSchema>;

const AssistantReportDefinitionRouteIntentSchema = z.custom<AssistantReportDefinition>(
  (value) => parseAssistantReportDefinition(value) !== null,
);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const readStringArray = (value: unknown): string[] => (
  Array.isArray(value)
    ? value.map((entry) => String(entry ?? '').trim()).filter(Boolean)
    : []
);

const parseAssistantReportDefinition = (
  value: unknown,
): AssistantReportDefinition | null => {
  if (!isRecord(value)) {
    return null;
  }
  const title = String(value.title ?? '').trim();
  if (!title) {
    return null;
  }
  return {
    ...value,
    title,
    pathname: typeof value.pathname === 'string' ? value.pathname.trim() || null : null,
    module: typeof value.module === 'string'
      ? value.module as AssistantReportDefinition['module']
      : undefined,
    routeFamily: typeof value.routeFamily === 'string'
      ? value.routeFamily as AssistantReportDefinition['routeFamily']
      : undefined,
    templateId: typeof value.templateId === 'string' ? value.templateId.trim() || null : null,
    templateSource: value.templateSource === 'gallery' || value.templateSource === 'custom'
      ? value.templateSource
      : null,
    selectedEntities: readStringArray(value.selectedEntities),
    selectedFields: readStringArray(value.selectedFields),
    conditions: Array.isArray(value.conditions)
      ? value.conditions as AssistantReportDefinition['conditions']
      : [],
    dateRange: isRecord(value.dateRange)
      ? value.dateRange as AssistantReportDefinition['dateRange']
      : { preset: 'Custom' },
    sorts: Array.isArray(value.sorts)
      ? value.sorts as AssistantReportDefinition['sorts']
      : [],
  };
};

export const RunReportsVisualizationRouteIntentSchema = z.object({
  kind: z.literal(RUN_REPORTS_VISUALIZATION_ROUTE_INTENT_KIND),
  sourceFragmentId: z.string().trim().min(1).nullable().optional(),
  reportDefinition: AssistantReportDefinitionRouteIntentSchema,
  visualization: z.object({
    bodySurface: z.literal('visual').default('visual'),
    view: RunReportsVisualizationViewSchema.default('chart'),
    chartType: RunReportsRouteIntentChartTypeSchema.nullable().optional(),
    templateId: z.string().trim().min(1).nullable().optional(),
    templateMappingId: z.string().trim().min(1).nullable().optional(),
    useCaseId: z.string().trim().min(1).nullable().optional(),
  }).strict(),
}).strict();

export type RunReportsVisualizationRouteIntent = z.infer<
  typeof RunReportsVisualizationRouteIntentSchema
>;

export type RouteFragmentIntentHandoff = {
  kind: typeof RUN_REPORTS_VISUALIZATION_ROUTE_INTENT_KIND;
  fragmentId: string;
  label: string;
  sourcePathname: string;
  targetPathname: string;
  actionLabel: string;
  href: string;
  sourceRef?: string | null;
  preview?: string | null;
};

const readParams = (value: URLSearchParams | string): URLSearchParams => (
  typeof value === 'string'
    ? new URLSearchParams(value.startsWith('?') ? value.slice(1) : value)
    : value
);

export const normalizeRouteFragmentId = (value: string | null | undefined): string =>
  String(value ?? '')
    .trim()
    .replace(/^#/, '')
    .trim();

export const resolveRouteFragmentIntentHandoff = (
  handoffs: readonly RouteFragmentIntentHandoff[],
  fragment: string | null | undefined,
): RouteFragmentIntentHandoff | null => {
  const fragmentId = normalizeRouteFragmentId(fragment);
  if (!fragmentId) {
    return null;
  }
  return handoffs.find((handoff) => handoff.fragmentId === fragmentId) ?? null;
};

export const buildRunReportsVisualizationRouteIntent = (input: {
  sourceFragmentId?: string | null;
  reportDefinition: AssistantReportDefinition;
  view?: RunReportsVisualizationView;
  chartType?: RunReportsRouteIntentChartType | null;
  templateId?: string | null;
  templateMappingId?: string | null;
  useCaseId?: string | null;
}): RunReportsVisualizationRouteIntent => (
  RunReportsVisualizationRouteIntentSchema.parse({
    kind: RUN_REPORTS_VISUALIZATION_ROUTE_INTENT_KIND,
    sourceFragmentId: input.sourceFragmentId ?? null,
    reportDefinition: input.reportDefinition,
    visualization: {
      bodySurface: 'visual',
      view: input.view ?? 'chart',
      chartType: input.chartType ?? null,
      templateId: input.templateId ?? input.reportDefinition.templateId ?? null,
      templateMappingId: input.templateMappingId ?? null,
      useCaseId: input.useCaseId ?? null,
    },
  })
);

export const buildRunReportsVisualizationRouteIntentSearchParams = (
  intent: RunReportsVisualizationRouteIntent,
): URLSearchParams => {
  const parsed = RunReportsVisualizationRouteIntentSchema.parse(intent);
  const params = new URLSearchParams();
  params.set(
    RUN_REPORTS_REPORT_DEFINITION_QUERY_PARAM,
    JSON.stringify(parsed.reportDefinition),
  );
  params.set(RUN_REPORTS_ROUTE_INTENT_QUERY_PARAM, parsed.kind);
  if (parsed.sourceFragmentId) {
    params.set(RUN_REPORTS_ROUTE_INTENT_SOURCE_FRAGMENT_QUERY_PARAM, parsed.sourceFragmentId);
  }
  params.set(RUN_REPORTS_VISUALIZATION_VIEW_QUERY_PARAM, parsed.visualization.view);
  if (parsed.visualization.chartType) {
    params.set(RUN_REPORTS_VISUALIZATION_CHART_TYPE_QUERY_PARAM, parsed.visualization.chartType);
  }
  return params;
};

export const buildRunReportsVisualizationRouteIntentHref = (
  intent: RunReportsVisualizationRouteIntent,
  pathname = '/school/run-reports',
): string => {
  const params = buildRunReportsVisualizationRouteIntentSearchParams(intent);
  return `${pathname}?${params.toString()}#${RUN_REPORTS_VISUALIZATION_HASH}`;
};

export const readRunReportsVisualizationRouteIntentFromSearch = (
  paramsLike: URLSearchParams | string,
): RunReportsVisualizationRouteIntent | null => {
  const params = readParams(paramsLike);
  if (
    params.get(RUN_REPORTS_ROUTE_INTENT_QUERY_PARAM) !==
    RUN_REPORTS_VISUALIZATION_ROUTE_INTENT_KIND
  ) {
    return null;
  }

  const rawDefinition = params.get(RUN_REPORTS_REPORT_DEFINITION_QUERY_PARAM);
  if (!rawDefinition) {
    return null;
  }

  try {
    const reportDefinition = parseAssistantReportDefinition(JSON.parse(rawDefinition));
    if (!reportDefinition) {
      return null;
    }
    return buildRunReportsVisualizationRouteIntent({
      sourceFragmentId: params.get(RUN_REPORTS_ROUTE_INTENT_SOURCE_FRAGMENT_QUERY_PARAM),
      reportDefinition,
      view: RunReportsVisualizationViewSchema.catch('chart').parse(
        params.get(RUN_REPORTS_VISUALIZATION_VIEW_QUERY_PARAM) ?? 'chart',
      ),
      chartType: params.has(RUN_REPORTS_VISUALIZATION_CHART_TYPE_QUERY_PARAM)
        ? RunReportsRouteIntentChartTypeSchema.catch('bar').parse(
            params.get(RUN_REPORTS_VISUALIZATION_CHART_TYPE_QUERY_PARAM),
          )
        : null,
      templateId: reportDefinition.templateId ?? null,
    });
  } catch {
    return null;
  }
};
